import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type { Select2Option } from '@/components/forms/Select2';
import type {
  BulkUpdateProviderResult,
  BulkUpdateTransactionProviderInput,
  MoneyTransactionRow,
  MoneyTransactionsAnalytics,
  MoneyTransactionsInput,
  SelectListItemDto,
  UpdateTransactionProviderInput,
} from '@/api/types/moneyTransactions';

// Finkit.Tenants.User.DailyReconciliation.MoneyTransactions.MoneyTransactionsAppService.
// ABP conventional routing: the HTTP-verb prefix is stripped (Get -> GET, Update -> PUT,
// non-verb prefixes default to POST); a primitive param named `id` goes in the path.
const BASE = '/api/app/money-transactions';

// Repeated-param array serialization (connector=1&connector=2), matching the other
// server-paged services; ABP binds this to List<T>.
const SERIALIZE = { paramsSerializer: { indexes: null as null } };

function toOptions(items: SelectListItemDto[]): Select2Option[] {
  return (items ?? []).map((i) => ({ value: i.value, label: i.text }));
}

export const moneyTransactionsApi = {
  getForDataTable: (input: MoneyTransactionsInput) =>
    http
      .get<PagedResult<MoneyTransactionRow>>(`${BASE}/for-data-table`, { params: input, ...SERIALIZE })
      .then((r) => r.data),

  getAnalytics: (input: MoneyTransactionsInput) =>
    http
      .get<MoneyTransactionsAnalytics>(`${BASE}/analytics`, { params: input, ...SERIALIZE })
      .then((r) => r.data),

  extractExcelUrl: `${BASE}/extract-excel`,

  updateProvider: (input: UpdateTransactionProviderInput) =>
    http.put<void>(`${BASE}/provider`, input).then((r) => r.data),

  hasPendingProviderChange: (id: number) =>
    http.post<boolean>(`${BASE}/has-pending-provider-change/${id}`).then((r) => r.data),

  // BulkUpdateProviderAsync(dto) — non-Get + complex DTO -> POST with JSON body.
  bulkUpdateProvider: (input: BulkUpdateTransactionProviderInput) =>
    http.post<BulkUpdateProviderResult>(`${BASE}/bulk-update-provider`, input).then((r) => r.data),

  getProviderOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/provider-options`).then((r) => toOptions(r.data)),
  getProviderTypeOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/provider-type-options`).then((r) => toOptions(r.data)),
  getCurrencyOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/currency-options`).then((r) => toOptions(r.data)),
  getTransactionTypeOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/transaction-type-options`).then((r) => toOptions(r.data)),
  getAmountOperatorOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/amount-operator-options`).then((r) => toOptions(r.data)),
  getRiskOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/risk-options`).then((r) => toOptions(r.data)),
  getMatchStatusOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/match-status-options`).then((r) => toOptions(r.data)),
  getMatchTypeOptions: () =>
    http.get<SelectListItemDto[]>(`${BASE}/match-type-options`).then((r) => toOptions(r.data)),
};
